import React, { createContext, useContext, useState, useEffect } from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  username: string | null;
  password: string | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);

  // Check if user is already logged in on mount
  useEffect(() => {
    const storedUsername = sessionStorage.getItem('auth_username');
    const storedPassword = sessionStorage.getItem('auth_password');
    
    if (storedUsername && storedPassword) {
      setUsername(storedUsername);
      setPassword(storedPassword);
      setIsAuthenticated(true);
    }
  }, []);

  const login = async (inputUsername: string, inputPassword: string): Promise<boolean> => {
    // Test credentials by making a simple API call
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/teachers/`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${inputUsername}:${inputPassword}`)
        }
      });

      if (response.ok) {
        // Credentials are valid
        setUsername(inputUsername);
        setPassword(inputPassword);
        setIsAuthenticated(true);
        sessionStorage.setItem('auth_username', inputUsername);
        sessionStorage.setItem('auth_password', inputPassword);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  };

  const logout = () => {
    setUsername(null);
    setPassword(null);
    setIsAuthenticated(false);
    sessionStorage.removeItem('auth_username');
    sessionStorage.removeItem('auth_password');
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, username, password, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
